import type { GeoPoint, Mission, MissionState, UnlockMethod } from './types.js';
import { unlockInstantIso } from './time.js';
import { haversineMeters, isShortMapsUrl, parseMapsUrl } from './geo.js';
import { isManualEnabled } from './sheetMapping.js';

export interface UnlockPosition {
  lat: number;
  lng: number;
  accuracy?: number;
}

/** הקשר ההערכה — זמן נוכחי, מיקום נוכחי, רדיוס ומשימות שכבר נפתחו */
export interface UnlockContext {
  now: Date;
  position: UnlockPosition | null;
  radiusMeters: number;
  openedMissionIds: ReadonlySet<string>;
  /** אזור הזמן שבו נקראים התאריך והשעה של "אפשור זמן" (ברירת מחדל Asia/Jerusalem) */
  timeZone?: string;
}

export interface UnlockEvaluation {
  state: MissionState;
  /** אילו תנאים מתקיימים ברגע ההערכה */
  satisfied: UnlockMethod[];
  hasTimeCondition: boolean;
  hasLocationCondition: boolean;
  /** האם אפשור ידני מותר (מסומן V, או משימה ללא תנאים כלל) */
  manualAllowed: boolean;
  /** רגע האפשור המחושב (ISO UTC) אם קיים תנאי זמן תקין */
  unlockAtIso: string | null;
  /** יעד המיקום אם קיים תנאי מיקום מפוענח */
  target: GeoPoint | null;
  /** מרחק נוכחי מהיעד במטרים (אם ידועים מיקום ויעד) */
  distanceMeters: number | null;
  invalidReasons: string[];
}

/** יעד המיקום של משימה: קואורדינטות שפוענחו בשרת, או פענוח ישיר של הקישור */
export function missionTarget(m: Mission): GeoPoint | null {
  if (m.resolvedLat !== null && m.resolvedLng !== null) {
    return { lat: m.resolvedLat, lng: m.resolvedLng };
  }
  return parseMapsUrl(m.locationUrl);
}

/**
 * הערכת מצב משימה. שלושת מנגנוני הפתיחה פועלים לפי OR:
 * זמן (תאריך+שעה יחד), מיקום (בתוך הרדיוס), ידני (אושר על ידי המנהל).
 * משימה ללא אף תנאי נחשבת ידנית (13.4).
 */
export function evaluateMission(m: Mission, ctx: UnlockContext): UnlockEvaluation {
  const hasTimeCondition = m.unlockTimeRaw.trim() !== '';
  const hasLocationCondition = m.locationUrl.trim() !== '';
  const unlockAtIso = hasTimeCondition ? unlockInstantIso(m.date, m.unlockTimeRaw, ctx.timeZone) : null;
  const target = hasLocationCondition ? missionTarget(m) : null;
  const manualAllowed = isManualEnabled(m.manualRaw) || (!hasTimeCondition && !hasLocationCondition);

  const satisfied: UnlockMethod[] = [];
  const invalidReasons: string[] = [];

  if (hasTimeCondition) {
    if (unlockAtIso) {
      if (ctx.now.getTime() >= Date.parse(unlockAtIso)) satisfied.push('time');
    } else {
      invalidReasons.push('תנאי הזמן אינו תקין — נדרשים תאריך ושעה תקינים');
    }
  }

  let distanceMeters: number | null = null;
  if (hasLocationCondition) {
    if (target) {
      if (ctx.position) {
        distanceMeters = haversineMeters(ctx.position, target);
        if (distanceMeters <= ctx.radiusMeters) satisfied.push('location');
      }
    } else {
      invalidReasons.push(
        isShortMapsUrl(m.locationUrl)
          ? 'קישור המיקום מקוצר וממתין לפענוח בשרת'
          : 'קישור המיקום אינו מזוהה',
      );
    }
  }

  if (manualAllowed && m.manuallyUnlockedAt) satisfied.push('manual');

  const state: MissionState = ctx.openedMissionIds.has(m.id)
    ? 'opened'
    : satisfied.length > 0
      ? 'available'
      : 'locked';

  return {
    state,
    satisfied,
    hasTimeCondition,
    hasLocationCondition,
    manualAllowed,
    unlockAtIso,
    target,
    distanceMeters,
    invalidReasons,
  };
}

/** עדיפות כאשר תנאי הפך זה עתה למתקיים: פעולה ידנית > הגעה למיקום > זמן */
const NEWLY_PRIORITY: UnlockMethod[] = ['manual', 'location', 'time'];
/** עדיפות כשחוזרים לאפליקציה וכמה תנאים כבר מתקיימים: ידני > זמן > מיקום */
const RESUME_PRIORITY: UnlockMethod[] = ['manual', 'time', 'location'];

/**
 * בחירת שיטת הפתיחה שתירשם באירוע: מעדיפים את התנאי שהתקיים זה עתה
 * (לעומת ההערכה הקודמת); אם אין כזה — לפי סדר עדיפות קבוע.
 */
export function pickUnlockMethod(
  prevSatisfied: UnlockMethod[] | null,
  currentSatisfied: UnlockMethod[],
): UnlockMethod {
  // prevSatisfied === null פירושו שאין הערכה קודמת (חזרה לאפליקציה) — אין "חדש"
  const prev = new Set(prevSatisfied ?? []);
  const newly =
    prevSatisfied === null ? [] : currentSatisfied.filter((s) => !prev.has(s));
  const pool = newly.length > 0 ? newly : currentSatisfied;
  const order = newly.length > 0 ? NEWLY_PRIORITY : RESUME_PRIORITY;
  for (const method of order) {
    if (pool.includes(method)) return method;
  }
  return pool[0] ?? 'manual';
}

/** תווית עברית לשיטת פתיחה */
export function unlockMethodLabel(method: UnlockMethod): string {
  switch (method) {
    case 'time':
      return 'זמן';
    case 'location':
      return 'מיקום';
    case 'manual':
      return 'ידני';
    case 'solution':
      return 'פתרון';
  }
}

/** אימוג'י לשיטת פתיחה (לפי אפיון מפת הפתיחות) */
export function unlockMethodEmoji(method: UnlockMethod): string {
  switch (method) {
    case 'time':
      return '⏰';
    case 'location':
      return '📍';
    case 'manual':
      return '🟢';
    case 'solution':
      return '🧩';
  }
}
